<template>
	<portal class="vue-chat" to="chat">
		<ButtonIcon
			@click="toggleModal"
			:iconClassName="iconChatButton"
			:iconBeforeLabel="true"
			class="chat-button"
			variant="outline"
			v-if="isVisibleOnFeatureSwitch"
		/>

		<div ref="modal" v-if="isModalOpen" class="chat-modal">
			<div class="chat-modal__container">
				<div
					ref="header"
					class="chat-modal__header"
					@mousedown="onMouseDown"
				>
					<VUELogoVonly class="chat-modal__header__logo" />
					<p class="chat-modal__header-title">
						VUE Virtual Assistant
					</p>

					<div class="chat-modal__header__button-container">
						<ButtonIcon
							ref="newChat"
							@click="initChat"
							:iconClassName="'icon-edit'"
							:iconBeforeLabel="true"
							class="purge-button"
							variant="outline"
							:disabled="!hasChatHistory"
							:title="'Start a New Chat'"
						/>
					</div>

					<div class="chat-modal__header__button-container">
						<ButtonIcon
							:title="'Close'"
							@click="toggleModal"
							:iconClassName="iconChatButton"
							:iconBeforeLabel="true"
							class="close-button"
							variant="outline"
						/>
					</div>
				</div>
				<InfiniteScroller
					class="chat-model__infinity-scroll"
					ref="scrollContainer"
				>
					<template v-slot:scrollDiv>
						<VueChatMessageList
							:messages="allMessages"
							@message-list-changed="scrollToBottom"
						/>
					</template>
				</InfiniteScroller>
				<VueChatInput
					class="chat-modal__input"
					:value="search"
					@submit="fetchDataMessage"
				/>
			</div>
		</div>
	</portal>
</template>

<script>
import ButtonIcon from '@/components/forms/button/button-icon.vue';
import VueChatInput from './components/vue-chat-input.vue';
import baseComponentMixin from '@/mixins/base-component-mixin';
import VueChatMessageList from './components/vue-chat-message-list.vue';
import VUELogoVonly from '@/assets/icons/vue_logo-v-only.svg?inline';
import { mapGetters, mapActions, mapState } from 'vuex';
import InfiniteScroller from '@/components/shells/infinite-scroller/infinite-scroller.vue';
import {
	CHAT,
	RETRIEVE_MESSAGES,
	KEY_BY_ASPNETCOMPANYID_EMPLOYEEID,
	GET_SPEECH_TOKEN
} from '@/store/modules/chat/chat-mutation-types';
import _ from 'lodash';

export default {
	name: 'VueChatAI',
	mixins: [baseComponentMixin],
	components: {
		ButtonIcon,
		VueChatInput,
		VUELogoVonly,
		VueChatMessageList,
		InfiniteScroller
	},

	data() {
		return {
			isModalOpen: false,
			search: ''
		};
	},
	computed: {
		...mapGetters('chat', [CHAT]),
		...mapState('chat', [KEY_BY_ASPNETCOMPANYID_EMPLOYEEID]),

		iconChatButton() {
			if (this.isModalOpen) {
				return 'icon icon-remove-2';
			}
			return 'icon-chat';
		},
		allMessages() {
			return _.filter(this[CHAT] || [], chat =>
				_.includes(
					['system-error', 'assistant', 'user'],
					_.get(chat, 'role.label', null)
				)
			);
		},
		hasChatHistory() {
			return this.allMessages?.length > 1;
		}
	},
	methods: {
		...mapActions('chat', [RETRIEVE_MESSAGES, GET_SPEECH_TOKEN]),

		toggleModal() {
			this.isModalOpen = !this.isModalOpen;
			if (this.isModalOpen) {
				this.$store.dispatch('chat/Init', {
					customErrorHandler: this.handleCustomError
				});
			}
		},
		initChat() {
			this.$nextTick(() => {
				const container = this.$refs.scrollContainer.$el;
				container.scrollTop = 0;
				this.$store.commit('chat/SaveMessage', []);

				localStorage.removeItem(
					this[KEY_BY_ASPNETCOMPANYID_EMPLOYEEID]
				);
				this.$store.dispatch('chat/Init', {
					customErrorHandler: this.handleCustomError
				});
			});
		},
		fetchDataMessage(message) {
			this.search = '';
			this[RETRIEVE_MESSAGES]({
				message,
				customErrorHandler: this.handleCustomError
			});
		},
		handleCustomError(error) {
			const messageError = {
				default:
					'A system error has occurred. Please send your message again. If this issue persists, please contact us through the Dominion DMS Help Center at help.dominiondms.com',
				400: 'Bad request',
				500: 'Internal Server Error.'
			};

			const lastId = this[CHAT].reduce(
				(maxId, msg) => Math.max(maxId, msg.id || 0),
				0
			);
			const errorMessage = {
				content: error
					? messageError.default
					: ' Please send your message again.',
				role: {
					label: 'system-error'
				},
				timestamp: new Date().toISOString(),
				id: lastId + 1
			};
			const allMessages = this[CHAT]
				? [...this[CHAT], errorMessage]
				: [errorMessage];
			this.$store.commit('chat/SaveMessage', allMessages);
			localStorage.setItem(
				this[KEY_BY_ASPNETCOMPANYID_EMPLOYEEID],
				JSON.stringify(allMessages)
			);
		},
		onMouseDown(event) {
			event.preventDefault();
			const header = this.$refs.header;
			const modal = this.$refs.modal;
			const shiftX = event.clientX - modal.getBoundingClientRect().left;
			const shiftY = event.clientY - modal.getBoundingClientRect().top;

			header.classList.add('dragging');

			const onMouseMove = _.throttle(event => {
				let newLeft = event.pageX - shiftX;
				let newTop = event.pageY - shiftY;

				// Limit the movement within the boundaries of the window
				const rightEdge =
					document.documentElement.clientWidth - modal.offsetWidth;
				const bottomEdge =
					document.documentElement.clientHeight - modal.offsetHeight;

				if (newLeft < 0) newLeft = 0;
				if (newTop < 0) newTop = 0;
				if (newLeft > rightEdge) newLeft = rightEdge;
				if (newTop > bottomEdge) newTop = bottomEdge;

				modal.style.left = newLeft + 'px';
				modal.style.top = newTop + 'px';
			}, 16); // Adjust delay (in ms) as needed for smooth performance

			document.addEventListener('mousemove', onMouseMove);

			document.addEventListener(
				'mouseup',
				() => {
					header.classList.remove('dragging');
					document.removeEventListener('mousemove', onMouseMove);
				},
				{ once: true }
			);
		},
		scrollToBottom() {
			this.$nextTick(() => {
				const container = this.$refs.scrollContainer.$el;
				container.scrollTop = container.scrollHeight;
			});
		}
	}
};
</script>

<style lang="scss" scoped>
.vue-chat {
	font-family: 'Lato', sans-serif;

	.chat-button {
		@include medium-shadow;
		position: fixed;
		bottom: 20px;
		right: 20px;
		width: 60px;
		height: 60px;
		color: #f9f9f9;
		background-color: #009245;
		font-size: 28px;
		border: none;
		outline: 0px;
		z-index: $z-level7;

		@include mq($until: 1575px) {
			width: 32px;
			height: 32px;
			font-size: 16px;
		}

		&:hover,
		&:focus,
		&:active {
			background-color: #136a3e;
			color: #f9f9f9;
			outline: 0px;

			&:after {
				border: none;
			}

			.icon:after {
				border-bottom: transparent;
				font-size: 24px;
			}
		}
	}

	.chat-modal {
		position: fixed;
		bottom: 92px;
		right: 20px;
		width: 400px;
		height: 500px;
		border: 1px solid var(--neutral-2);
		border-radius: $border-radius;
		z-index: $z-level7;

		@include mq($until: md) {
			left: 0;
			top: 0;
			width: 100%;
			height: 100vh;
			border: none;
			border-radius: none;
		}

		&__container {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			background-color: var(--bg-0);
			padding: $padding-md;
			border-bottom-left-radius: $border-radius;
			border-bottom-right-radius: $border-radius;
			box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
			height: calc(100% - 50px);
			margin-top: 50px;
		}

		&__header {
			display: flex;
			align-items: center;
			background-color: #009245;
			border-top-left-radius: $border-radius;
			border-top-right-radius: $border-radius;
			justify-content: space-between;
			height: 50px;
			width: 100%;
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			cursor: grab; // check cursor
			padding: 0 $padding-sm 0 $padding-sm;

			&.dragging {
				cursor: grabbing;
			}

			&-title {
				color: var(--neutral-0);
				font-size: $heading-font-size-md;
				padding: $padding-sm $padding-sm $padding-sm 0;
				margin: 0;
				width: 68%;
			}

			&__logo {
				height: 30px;
				width: 30px;
				margin: $gutter-md;
			}

			&__button-container {
				display: flex;
				justify-content: flex-end;
				.close-button {
					color: var(--neutral-0);
					background-color: #009245;
					box-shadow: none;
					border: none;
					border-radius: 50%;

					&:hover,
					&:focus,
					&:active {
						outline: 0px;
						background-color: #136a3e;

						&:after {
							border: none;
						}
					}
				}
				.purge-button {
					color: var(--neutral-0);
					background-color: #009245;
					box-shadow: none;
					border: none;
					border-radius: 50%;

					&:hover,
					&:focus,
					&:active {
						outline: 0px;
						background-color: #136a3e;

						&:after {
							border: none;
						}
					}
				}
			}
		}

		&__infinity-scroll::v-deep {
			.infinite-scroller__container-elements {
				margin: $gutter-lg 0 0;
			}

			.infinite-scroller__loading {
				display: flex;
			}
		}

		&__input {
			margin-top: $gutter-md;
		}
	}
}
</style>
